import React, { useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import config from '../../config';

export default function Orders() {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [availableRiders, setAvailableRiders] = useState([]);
    const [loadingRiders, setLoadingRiders] = useState(false);
    const [assigningRider, setAssigningRider] = useState(false);
    const { API_URL } = config;

    const fetchOrders = async (page = 1, limit = 10) => {
        try {
            setLoadingMore(true);
            const ordersRes = await fetch(`${API_URL}/api/order/getAllOrders?page=${page}&limit=${limit}`);
            const ordersData = await ordersRes.json();
            
            if (ordersData.orders.length > 0) {
                // Sort orders by date in descending order (newest first)
                const sortedOrders = ordersData.orders.sort((a, b) => new Date(b.date) - new Date(a.date));

                // For each order, fetch user details to get phone number
                const ordersWithContacts = await Promise.all(sortedOrders.map(async (order) => {
                    try {
                        const userRes = await fetch(`${API_URL}/api/auth/getUserByID/${order.orderByid}`, {
                            method: 'POST'
                        });
                        const userData = await userRes.json();
                        return {
                            ...order,
                            customerContact: userData.executed ? userData.user.contact : 'N/A'
                        };
                    } catch (error) {
                        console.error('Error fetching user details:', error);
                        return {
                            ...order,
                            customerContact: 'N/A'
                        };
                    }
                }));

                if (page === 1) {
                    setOrders(ordersWithContacts);
                    setFilteredOrders(ordersWithContacts);
                } else {
                    setOrders(prev => [...prev, ...ordersWithContacts]);
                    setFilteredOrders(prev => [...prev, ...ordersWithContacts]);
                }
                setHasMore(ordersData.hasMore);
                setCurrentPage(ordersData.currentPage);
            }
            setLoading(false);
            setLoadingMore(false);
        } catch (error) {
            console.error('Error fetching orders:', error);
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchAvailableRiders = async (order) => {
        console.log('🔍 fetchAvailableRiders called');
        console.log('📦 Full order object:', JSON.stringify(order, null, 2));
        try {
            setLoadingRiders(true);
            
            // Get restaurant city from the order - try different possible fields
            let restaurantCity = order.restaurantCity || order.city || order.restaurantcity;
            console.log('🏙️ City from order object:', restaurantCity);
            console.log('🔍 Checking order fields:', {
                restaurantCity: order.restaurantCity,
                city: order.city,
                restaurantcity: order.restaurantcity,
                Restaurantuid: order.Restaurantuid
            });
            
            // If not in order, fetch restaurant details
            if (!restaurantCity) {
                console.log('⚠️ No city in order, fetching restaurant details for:', order.Restaurantuid);
                const restaurantRes = await fetch(`${API_URL}/api/auth/getRestaurantUser/${order.Restaurantuid}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });
                const restaurantData = await restaurantRes.json();
                console.log('🏪 Restaurant data:', restaurantData);
                console.log('🏪 Restaurant user object:', restaurantData.user);
                // API returns { user: { ... } } not { restaurant: { ... } }
                restaurantCity = restaurantData.user?.city || restaurantData.restaurant?.city || restaurantData.city;
            }
            
            console.log('🏙️ Final restaurant city:', restaurantCity);
            
            // Now fetch all delivery boys
            console.log('📡 Fetching riders from:', `${API_URL}/api/auth/getAllDeliveryBoys`);
            const response = await fetch(`${API_URL}/api/auth/getAllDeliveryBoys`);
            const data = await response.json();
            console.log('✅ Total riders received:', data.deliveryBoys?.length);
            
            if (data.deliveryBoys && Array.isArray(data.deliveryBoys)) {
                // Filter riders who are verified, not banned, and in the same city as restaurant
                const activeRiders = data.deliveryBoys.filter(rider => {
                    const isVerified = rider.status === 'verified';
                    const isNotBanned = !rider.isBanned;
                    
                    // Normalize city names for comparison (trim spaces, lowercase)
                    const riderCity = (rider.city || '').trim().toLowerCase();
                    const targetCity = (restaurantCity || '').trim().toLowerCase();
                    const isSameCity = restaurantCity ? riderCity === targetCity : true;
                    
                    console.log(`👤 ${rider.firstName} ${rider.lastName}: verified=${isVerified}, banned=${rider.isBanned}, city="${rider.city}" (normalized: "${riderCity}"), target="${restaurantCity}" (normalized: "${targetCity}"), matches=${isSameCity}`);
                    
                    return isVerified && isNotBanned && isSameCity;
                });
                console.log('✅ Active riders in', restaurantCity || 'all cities', ':', activeRiders.length);
                console.log('📋 Filtered riders:', activeRiders.map(r => `${r.firstName} ${r.lastName} (${r.city})`));
                setAvailableRiders(activeRiders);
            } else {
                console.log('⚠️ No riders found - data.deliveryBoys is:', data.deliveryBoys);
                setAvailableRiders([]);
            }
            setLoadingRiders(false);
        } catch (error) {
            console.error('❌ Error fetching riders:', error);
            alert('Failed to fetch riders: ' + error.message);
            setLoadingRiders(false);
        }
    };

    const assignRiderToOrder = async (riderId, riderName) => {
        console.log('👤 assignRiderToOrder called', { riderId, riderName, orderId: selectedOrder._id });
        if (!window.confirm(`Assign ${riderName} to this order?`)) {
            console.log('❌ User cancelled assignment');
            return;
        }
        
        try {
            setAssigningRider(true);
            console.log('📡 Assigning rider, POST to:', `${API_URL}/api/order/assignRider`);
            console.log('📦 Sending order _id:', selectedOrder._id);
            
            // Update order with delivery boy UID
            const response = await fetch(`${API_URL}/api/order/assignRider`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderId: selectedOrder._id,
                    deliveryBoyuid: riderId
                })
            });
            
            const result = await response.json();
            console.log('✅ Assignment response:', result);
            console.log('✅ result.executed:', result.executed);
            console.log('✅ result.message:', result.message);
            console.log('✅ Full response status:', response.status);
            
            if (result.executed) {
                alert('Rider assigned successfully! ' + (result.message || ''));
                setSelectedOrder(null);
                fetchOrders(1); // Refresh orders
            } else {
                alert('Failed to assign rider: ' + (result.message || 'Unknown error'));
            }
            setAssigningRider(false);
        } catch (error) {
            console.error('❌ Error assigning rider:', error);
            alert('Failed to assign rider');
            setAssigningRider(false);
        }
    };

    const broadcastToRiders = async () => {
        console.log('📢 broadcastToRiders called', { orderId: selectedOrder._id, restaurantId: selectedOrder.Restaurantuid });
        if (!window.confirm('Send notification to all riders in the area?')) {
            console.log('❌ User cancelled broadcast');
            return;
        }
        
        try {
            console.log('📡 Broadcasting, POST to:', `${API_URL}/api/order/broadcastOrder`);
            const response = await fetch(`${API_URL}/api/order/broadcastOrder`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderId: selectedOrder._id,
                    restaurantId: selectedOrder.Restaurantuid
                })
            });
            
            const result = await response.json();
            console.log('✅ Broadcast response:', result);
            if (result.executed) {
                alert('Notification sent to all riders!');
            } else {
                alert('Failed to send notifications: ' + (result.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('❌ Error broadcasting:', error);
            alert('Failed to broadcast notification');
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [API_URL]);

    const handleSearch = (event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);

        const filtered = orders.filter((order) =>
            order.orderId.toLowerCase().includes(term) ||
            order.orderByName.toLowerCase().includes(term) ||
            (order.customerContact && order.customerContact.includes(term)) ||
            order.paymentMethod.toLowerCase().includes(term) ||
            order.status.toLowerCase().includes(term)
        );

        setFilteredOrders(filtered);
    };

    // Infinite scroll handler
    const handleLoadMore = () => {
        if (!loadingMore && hasMore) {
            fetchOrders(currentPage + 1);
        }
    };

    // Custom row component to handle intersection observer
    const CustomRow = React.memo(({ row, ...props }) => {
        const rowRef = useRef();
        
        useEffect(() => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && props.isLastRow) {
                        handleLoadMore();
                    }
                },
                { threshold: 0.5 }
            );

            if (rowRef.current) {
                observer.observe(rowRef.current);
            }

            return () => {
                if (rowRef.current) {
                    observer.unobserve(rowRef.current);
                }
            };
        }, [props.isLastRow]);

        return (
            <div ref={rowRef}>
                {props.children}
            </div>
        );
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'Accepted':
                return '#3B82F6';
            case 'Preparing':
                return '#F59E0B';
            case 'Completed':
                return '#10B981';
            case 'On the way':
                return '#F59E0B';
            case 'Delivered':
                return '#10B981';
            case 'Ready':
                return '#6366F1';
            case 'Declined':
                return 'red';
            default:
                return '#6B7280';
        }
    };

    const columns = [
        {
            name: 'Order ID',
            selector: (row) => row.orderId,
            sortable: true,
            width: "220px"
        },
        {
            name: 'Customer Name',
            selector: (row) => row.orderByName,
            sortable: true,
        },
        {
            name: 'Phone No.',
            selector: (row) => row.customerContact,
            sortable: true,
            cell: (row) => {
                const phone = row.customerContact;
                return phone ? phone.replace('+91', '').slice(-10) : 'N/A';
            }
        },
        {
            name: 'Payment Method',
            selector: (row) => row.paymentMethod,
            sortable: true,
        },
        {
            name: 'Status',
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <span
                    style={{
                        padding: '5px 10px',
                        textAlign: "center",
                        width: "110px",
                        borderRadius: '5px',
                        color: '#fff',
                        backgroundColor: getStatusColor(row.status),
                        fontWeight: '600',
                    }}
                >
                    {row.status}
                </span>
            ),
        },

        {
            name: 'Total Cost',
            selector: (row) => `₹${row.totalCost.toFixed(2)}`,
            sortable: true,
        },
        {
            name: 'Action',
            cell: (row) => (
                <button
                    onClick={() => setSelectedOrder(row)}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                    Details
                </button>
            ),
        },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-500" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const customStyles = {
        header: {
            style: {
                fontSize: '1.25rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                color: '#374151',
                backgroundColor: '#F9FAFB',
                borderBottom: '2px solid #E5E7EB',
            },
        },
        headCells: {
            style: {
                fontSize: '1rem',
                fontWeight: '600',
                color: '#1F2937',
                textTransform: 'uppercase',
                backgroundColor: '#F3F4F6',
                borderBottom: '1px solid #D1D5DB',
                padding: '10px',
            },
        },
        cells: {
            style: {
                fontSize: '0.975rem',
                color: '#4B5563',
                borderBottom: '1px solid #E5E7EB',
                padding: '10px',
            },
        },
        rows: {
            style: {
                '&:hover': {
                    backgroundColor: '#F9FAFB',
                    cursor: 'pointer',
                },
            },
        },
        pagination: {
            style: {
                fontSize: '0.875rem',
                backgroundColor: '#F9FAFB',
                color: '#6B7280',
                borderTop: '1px solid #E5E7EB',
                paddingTop: '10px',
                paddingBottom: '10px',
                justifyContent: 'end',
            },
        },
    };

    return (
        <div className="p-6 h-screen flex flex-col">
            <h1 className="text-2xl font-bold mb-4">Orders</h1>
            <input
                type="text"
                placeholder="Search by Order ID, Customer Name, Phone Number..."
                value={searchTerm}
                onChange={handleSearch}
                className="border border-gray-300 px-4 py-2 w-full focus:outline-none focus:ring focus:ring-blue-200 mb-4 rounded-lg"
            />
            
            <div className="flex-grow overflow-auto">
                <DataTable
                    columns={columns}
                    data={filteredOrders}
                    pagination={false}
                    highlightOnHover
                    customStyles={customStyles}
                    noHeader
                    customRowComponent={(props) => (
                        <CustomRow
                            {...props}
                            isLastRow={props.dataIndex === filteredOrders.length - 1}
                        />
                    )}
                />
                {loadingMore && (
                    <div className="text-center py-4">
                        <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-500" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {selectedOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-10 z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-1/2 h-[90vh] overflow-y-auto">
                        <h2 className="text-2xl font-semibold mb-6 text-gray-800">Order Details</h2>

                        <div className="space-y-4">
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Order ID:</span>
                                <span className="text-gray-600">{selectedOrder.orderId}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Customer Name:</span>
                                <span className="text-gray-600">{selectedOrder.orderByName}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Order Type:</span>
                                <span className="text-gray-600">{selectedOrder.ordertype}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Payment Method:</span>
                                <span className="text-gray-600">{selectedOrder.paymentMethod}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Status:</span>
                                <span className="text-gray-600">{selectedOrder.status}</span>
                            </div>
                            
                            {/* Rider Assignment Section */}
                            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="font-semibold text-gray-700">Delivery Rider:</span>
                                    <span className="text-gray-600">
                                        {selectedOrder.deliveryBoyuid ? 
                                            `Assigned (${selectedOrder.deliveryBoyuid})` : 
                                            <span className="text-red-500 font-semibold">Not Assigned</span>
                                        }
                                    </span>
                                </div>
                                {!selectedOrder.deliveryBoyuid && (
                                    <div className="mt-4 space-y-2">
                                        <button
                                            onClick={() => {
                                                console.log('🖱️ Find Available Riders button clicked');
                                                fetchAvailableRiders(selectedOrder);
                                            }}
                                            disabled={loadingRiders}
                                            className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
                                        >
                                            {loadingRiders ? 'Loading Riders...' : '🔍 Find Available Riders'}
                                        </button>
                                        <button
                                            onClick={broadcastToRiders}
                                            className="w-full bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
                                        >
                                            📢 Broadcast to All Riders
                                        </button>
                                        
                                        {/* Debug info */}
                                        <div className="mt-2 p-2 bg-gray-100 rounded text-xs">
                                            <p>Debug: availableRiders.length = {availableRiders.length}</p>
                                            <p>loadingRiders = {loadingRiders.toString()}</p>
                                        </div>
                                        
                                        {console.log('🎨 Rendering rider list section, availableRiders.length:', availableRiders.length)}
                                        {availableRiders.length > 0 ? (
                                            <div className="mt-4 max-h-64 overflow-y-auto border-2 border-blue-500 rounded p-3 bg-white shadow-md">
                                                <p className="font-bold text-base mb-3 text-blue-600">Available Riders ({availableRiders.length}):</p>
                                                {availableRiders.map((rider, index) => (
                                                    <div key={rider._id} className="flex justify-between items-center p-3 mb-2 bg-gray-50 hover:bg-blue-50 rounded border">
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-gray-800">{rider.firstName} {rider.lastName}</p>
                                                            <p className="text-sm text-gray-600">{rider.city} • {rider.contact}</p>
                                                            <p className="text-xs text-gray-500">Status: {rider.status}</p>
                                                        </div>
                                                        <button
                                                            onClick={() => assignRiderToOrder(rider._id, `${rider.firstName} ${rider.lastName}`)}
                                                            disabled={assigningRider}
                                                            className="bg-green-500 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-green-600 disabled:bg-gray-400"
                                                        >
                                                            {assigningRider ? 'Assigning...' : 'Assign'}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-500">
                                                {loadingRiders ? 'Loading riders...' : 'Click "Find Available Riders" to see available delivery personnel'}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Total Cost:</span>
                                <span className="text-gray-600">₹{selectedOrder.totalCost.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Time:</span>
                                <span className="text-gray-600">{selectedOrder.time}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Time to Prepare:</span>
                                <span className="text-gray-600">{selectedOrder.timetoprepare}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-semibold text-gray-700">Date:</span>
                                <span className="text-gray-600">{new Date(selectedOrder.date).toLocaleDateString()}</span>
                            </div>
                        </div>

                        <div className="mt-6 border-t pt-4">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Items</h3>
                            <table className="min-w-full table-auto">
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Item Name</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Quantity</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Unit Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedOrder.items.map((item, index) => (
                                        <tr key={index} className="border-t">
                                            <td className="px-4 py-1 text-gray-600">{item.itemName}</td>
                                            <td className="px-4 py-1 text-gray-600">{item.itemQuantity}</td>
                                            <td className="px-4 py-1 text-gray-600">₹{item.unitCost.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-6 border-t pt-4">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Delivery Address</h3>
                            <p className="text-gray-600">{selectedOrder.deliveryAddress}</p>
                        </div>

                        <div className="mt-6 border-t pt-4">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Charges</h3>
                            <table className="min-w-full table-auto">
                                <thead>
                                    <tr>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Charge Type</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-t">
                                        <td className="px-4 py-2 text-gray-600">Convenience Fee</td>
                                        <td className="px-4 py-2 text-gray-600">₹{selectedOrder.convinenceFee}</td>
                                    </tr>
                                    <tr className="border-t">
                                        <td className="px-4 py-2 text-gray-600">Delivery Charge</td>
                                        <td className="px-4 py-2 text-gray-600">₹{selectedOrder.deliveryCharge}</td>
                                    </tr>
                                    <tr className="border-t">
                                        <td className="px-4 py-2 text-gray-600">GST</td>
                                        <td className="px-4 py-2 text-gray-600">₹{selectedOrder.gst.toFixed(2)}</td>
                                    </tr>
                                    <tr className="border-t">
                                        <td className="px-4 py-2 text-gray-600">Driver Tip</td>
                                        <td className="px-4 py-2 text-gray-600">₹{selectedOrder.drivertip}</td>
                                    </tr>
                                    <tr className="border-t">
                                        <td className="px-4 py-2 text-gray-600">Item Amount</td>
                                        <td className="px-4 py-2 text-gray-600">₹{selectedOrder.itemAmount}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => setSelectedOrder(null)}
                                className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
